import { z } from 'zod';

import { OWNER_ONLY_SCOPES, PERMISSION_SCOPES, type PermissionScope } from '../permissions';
import { PRESET_ROLES } from '../roles';

// ── primitives ───────────────────────────────────────────────────────────────
const TeamSlug = z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/i, {
  message: 'Slug: 1-40 chars, alphanumeric + dash, no leading/trailing dash',
});
const TeamName = z.string().min(1).max(80);
const ColorHex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const Email = z.string().email().max(254);
const Username = z.string().regex(/^[a-zA-Z0-9_-]{2,32}$/);

const PresetRole = z.enum(PRESET_ROLES);

/**
 * Permission scopes safe to assign to a custom role.  Excludes OWNER-only ones — the UI
 * hides those checkboxes; this gate is the floor.
 */
const SafeScope = z.enum(PERMISSION_SCOPES).refine((s) => !OWNER_ONLY_SCOPES.has(s), {
  message: 'OWNER-only scope cannot be granted to a custom role',
});

// ── teams CRUD ───────────────────────────────────────────────────────────────
export const CreateTeamSchema = z.object({
  slug: TeamSlug,
  name: TeamName,
  description: z.string().max(500).optional(),
  timezone: z.string().max(80).optional(),
});
export type CreateTeamInput = z.infer<typeof CreateTeamSchema>;

export const UpdateTeamSchema = z
  .object({
    name: TeamName.optional(),
    description: z.string().max(500).nullable().optional(),
    avatarUrl: z.string().url().nullable().optional(),
    coverImageUrl: z.string().url().nullable().optional(),
    timezone: z.string().max(80).optional(),
    aboutJson: z.unknown().optional(),         // TipTap JSON — opaque to the API
    require2fa: z.boolean().optional(),
    allowAdhocTags: z.boolean().optional(),
    /** Post "✓ Done: KEY — title" to the project's chat topic on completion. */
    chatPostTaskDone: z.boolean().optional(),
  })
  .strict();
export type UpdateTeamInput = z.infer<typeof UpdateTeamSchema>;

export const DeleteTeamSchema = z.object({
  confirmSlug: TeamSlug,
});
export type DeleteTeamInput = z.infer<typeof DeleteTeamSchema>;

export const TransferOwnershipSchema = z.object({
  newOwnerUsername: Username,
});
export type TransferOwnershipInput = z.infer<typeof TransferOwnershipSchema>;

// ── projection schemas (response) ────────────────────────────────────────────
export const TeamSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  kind: z.enum(['personal', 'regular']),
  timezone: z.string(),
  require2fa: z.boolean(),
  allowAdhocTags: z.boolean(),
  chatPostTaskDone: z.boolean(),
  billingPlan: z.string(),
  seatLimit: z.number().int().nonnegative(),
  createdAt: z.string(),
  archivedAt: z.string().nullable(),
});
export type TeamProjection = z.infer<typeof TeamSchema>;

export const TeamSummarySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  kind: z.enum(['personal', 'regular']),
  avatarUrl: z.string().nullable(),
  // Plan code (e.g. 'free' | 'team' | 'enterprise') so list/sidebar surfaces
  // can render a plan badge without a per-team billing fetch.
  billingPlan: z.string(),
  myRolePreset: PresetRole.nullable(),
  myCustomRoleId: z.string().uuid().nullable(),
  myCustomRoleName: z.string().nullable(),
  isFavorite: z.boolean(),
  /** Display name of the team's current OWNER (the OWNER-preset member), so
   *  cards can show "Owner: X" without a per-team fetch. Null for personal. */
  ownerName: z.string().nullable(),
});
export type TeamSummaryProjection = z.infer<typeof TeamSummarySchema>;

// ── memberships + invitations ────────────────────────────────────────────────
export const InviteMemberSchema = z
  .object({
    email: Email.optional(),
    username: Username.optional(),
    // OWNER is assigned only via transferOwnership, never granted on invite —
    // exclude it at the schema so it fails as a clean 400 (rather than slipping
    // through to the one_owner_per_team unique-index 500). The service also
    // rejects it defensively.
    rolePreset: PresetRole.exclude(['OWNER']).optional(),
    customRoleId: z.string().uuid().optional(),
  })
  .refine((v) => Boolean(v.email) !== Boolean(v.username), {
    message: 'Provide exactly one of email or username',
  })
  .refine((v) => Boolean(v.rolePreset) !== Boolean(v.customRoleId), {
    message: 'Provide exactly one of rolePreset or customRoleId',
  });
export type InviteMemberInput = z.infer<typeof InviteMemberSchema>;

export const ChangeMemberRoleSchema = z
  .object({
    rolePreset: PresetRole.optional(),
    customRoleId: z.string().uuid().optional(),
  })
  .refine((v) => Boolean(v.rolePreset) !== Boolean(v.customRoleId), {
    message: 'Provide exactly one of rolePreset or customRoleId',
  });
export type ChangeMemberRoleInput = z.infer<typeof ChangeMemberRoleSchema>;

export const MemberSchema = z.object({
  userId: z.string().uuid(),
  username: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  /** Chosen initials-avatar colour id (see AVATAR_COLOR_IDS); null = neutral. */
  avatarColor: z.string().nullable().optional(),
  rolePreset: PresetRole.nullable(),
  customRoleId: z.string().uuid().nullable(),
  customRoleName: z.string().nullable(),
  isActive: z.boolean(),
  joinedAt: z.string(),
});
export type MemberProjection = z.infer<typeof MemberSchema>;

export const InvitationSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  email: z.string(),
  rolePreset: PresetRole.nullable(),
  customRoleId: z.string().uuid().nullable(),
  invitedBy: z.string().uuid(),
  invitedByName: z.string(),
  expiresAt: z.string(),
  acceptedAt: z.string().nullable(),
  declinedAt: z.string().nullable(),
  createdAt: z.string(),
  /** W5-0 · client (Guest) invites only: the projects the client will see. Absent for member invites. */
  projectIds: z.array(z.string().uuid()).optional(),
});
export type InvitationProjection = z.infer<typeof InvitationSchema>;

// ── client (Guest) invites — W5-0 contract, W5-E implements ─────────────────

/** Most projects one client invite can open. */
export const CLIENT_INVITE_MAX_PROJECTS = 20;

/**
 * POST /api/teams/:teamSlug/invitations/client → `InvitationProjection`
 * (`rolePreset: 'VIEWER'`, `projectIds` echoed). Guards: TeamAccess +
 * `member.invite`; the service also requires `project.manage_access` on every
 * project. On accept (D6): each project becomes restricted if it isn't, and the
 * Guest gets `project_access` to exactly these — VIEWER has `project.view_all`,
 * so restriction is what keeps one client from seeing another's project.
 * Send an `Idempotency-Key`.
 */
export const InviteClientSchema = z
  .object({
    email: Email,
    projectIds: z
      .array(z.string().uuid())
      .min(1)
      .max(CLIENT_INVITE_MAX_PROJECTS)
      .refine((ids) => new Set(ids).size === ids.length, { message: 'Duplicate project id' }),
    /** Optional note shown in the invite email and on the invitation page. */
    message: z.string().trim().max(500).optional(),
  })
  .strict();
export type InviteClientInput = z.infer<typeof InviteClientSchema>;

/**
 * POST /api/invites/:token/accept and /api/invites/by-id/:id/accept.
 * `landing` (W5-0, client invites): the first granted project — the web opens
 * its Overview (client view) instead of the team page. Absent/null otherwise.
 */
export const AcceptInvitationResultSchema = z.object({
  teamId: z.string().uuid(),
  landing: z.object({ teamSlug: z.string(), projectSlug: z.string() }).nullable().optional(),
});
export type AcceptInvitationResult = z.infer<typeof AcceptInvitationResultSchema>;

/**
 * Pre-accept invitation preview.  Surfaced on `/invites/:token` so the
 * recipient can see team name, member count, who invited them, and what
 * role they'd join with — before clicking Accept.  Authorisation is
 * "knows the token" alone (same gate as accept); no email-match check
 * here so users without a Projoint account yet can still preview.
 */
export const InvitationPreviewSchema = z.object({
  teamName: z.string(),
  teamSlug: z.string(),
  teamMemberCount: z.number().int().min(0),
  inviterName: z.string(),
  inviterAvatarUrl: z.string().nullable(),
  invitedEmail: z.string(),
  rolePreset: PresetRole.nullable(),
  customRoleName: z.string().nullable(),
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  declinedAt: z.string().datetime().nullable(),
  /** W5-0 · client (Guest) invites only: names of the projects they'll see. Absent for member invites. */
  projectNames: z.array(z.string()).optional(),
  /** W5-0 · client invites: the inviter's note (`InviteClientInput.message`), if any. */
  message: z.string().nullable().optional(),
});
export type InvitationPreviewProjection = z.infer<typeof InvitationPreviewSchema>;

// ── custom roles ─────────────────────────────────────────────────────────────
export const CreateCustomRoleSchema = z.object({
  name: z.string().min(1).max(40),
  description: z.string().max(200).optional(),
  color: ColorHex.optional(),
  permissions: z.array(SafeScope).default([]),
  derivedFromPreset: PresetRole.default('MEMBER'),
});
export type CreateCustomRoleInput = z.infer<typeof CreateCustomRoleSchema>;

export const UpdateCustomRoleSchema = z
  .object({
    name: z.string().min(1).max(40).optional(),
    description: z.string().max(200).nullable().optional(),
    color: ColorHex.optional(),
    permissions: z.array(SafeScope).optional(),
    derivedFromPreset: PresetRole.optional(),
  })
  .strict();
export type UpdateCustomRoleInput = z.infer<typeof UpdateCustomRoleSchema>;

export const CustomRoleSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  color: z.string(),
  permissions: z.array(z.string()),
  derivedFromPreset: PresetRole,
  createdAt: z.string(),
});
export type CustomRoleProjection = z.infer<typeof CustomRoleSchema>;

/** Team-roles list combines presets and customs.  UI uses `kind` to discriminate. */
export const TeamRoleListItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('preset'),
    code: PresetRole,
    permissions: z.array(z.string()),
  }),
  z.object({
    kind: z.literal('custom'),
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    color: z.string(),
    derivedFromPreset: PresetRole,
    permissions: z.array(z.string()),
  }),
]);
export type TeamRoleListItem = z.infer<typeof TeamRoleListItemSchema>;

// ── settings ─────────────────────────────────────────────────────────────────
const StatusItem = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(40),
  color: ColorHex,
  category: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
  order: z.number().int().nonnegative(),
});
const PriorityItem = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(40),
  level: z.number().int().min(1).max(4),
  color: ColorHex,
});
const TaskTypeItem = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(40),
  icon: z.string().max(40).optional(),
  color: ColorHex.optional(),
});
const WorkingHours = z.object({
  monday: z.tuple([z.string(), z.string()]).nullable().optional(),
  tuesday: z.tuple([z.string(), z.string()]).nullable().optional(),
  wednesday: z.tuple([z.string(), z.string()]).nullable().optional(),
  thursday: z.tuple([z.string(), z.string()]).nullable().optional(),
  friday: z.tuple([z.string(), z.string()]).nullable().optional(),
  saturday: z.tuple([z.string(), z.string()]).nullable().optional(),
  sunday: z.tuple([z.string(), z.string()]).nullable().optional(),
});

export const TeamSettingsSchema = z.object({
  taskStatuses: z.array(StatusItem),
  priorities: z.array(PriorityItem),
  taskTypes: z.array(TaskTypeItem),
  tagPool: z.array(z.object({ id: z.string(), name: z.string(), color: ColorHex })),
  workingHours: WorkingHours.nullable(),
  allowAdhocTags: z.boolean(),
  require2fa: z.boolean(),
});
export type TeamSettingsProjection = z.infer<typeof TeamSettingsSchema>;

export const UpdateTeamSettingsSchema = z
  .object({
    taskStatuses: z.array(StatusItem).optional(),
    priorities: z.array(PriorityItem).optional(),
    taskTypes: z.array(TaskTypeItem).optional(),
    tagPool: z
      .array(z.object({ id: z.string(), name: z.string(), color: ColorHex }))
      .optional(),
    workingHours: WorkingHours.nullable().optional(),
    allowAdhocTags: z.boolean().optional(),
    require2fa: z.boolean().optional(),
  })
  .strict();
export type UpdateTeamSettingsInput = z.infer<typeof UpdateTeamSettingsSchema>;

// ── helper for response envelopes ────────────────────────────────────────────
export function isSafeScope(scope: string): scope is PermissionScope {
  return (PERMISSION_SCOPES as ReadonlyArray<string>).includes(scope) && !OWNER_ONLY_SCOPES.has(scope as PermissionScope);
}
